import { isResponse, readTokenLimitReached } from '@mummssoftware/utils';
import type { TokenStatus } from '@mummssoftware/utils';

import { RequestTimeoutError } from './requestTimeout';

/**
 * The utils Request returns the raw Response for any non-2xx status. Wrapping it
 * keeps the HTTP status available to the UI (403 -> "no access", 404 -> expired,
 * 409 -> already ended) without every hook re-checking isResponse().
 */
export class GatewayError extends Error {
  status: number;

  /** The unread error response, for the few bodies the widget reads (429). */
  response?: Response;

  constructor(status: number, message?: string, response?: Response) {
    super(message || `Request failed (${status})`);
    this.name = 'GatewayError';
    this.status = status;
    this.response = response;
  }
}

export const isGatewayError = (error: unknown): error is GatewayError =>
  error instanceof GatewayError;

/**
 * isResponse() is an instanceof check against node-fetch's class, which fails
 * whenever a different Response implementation is in play (jest, a future move
 * to native fetch). Fall back to the shape: a JSON payload from the gateway
 * never carries a numeric status plus ok/headers/text().
 */
export const isHttpResponse = (value: unknown): value is Response => {
  if (isResponse(value)) {
    return true;
  }
  if (!value || typeof value !== 'object') {
    return false;
  }
  const candidate = value as Partial<Response>;
  return (
    typeof candidate.status === 'number' &&
    typeof candidate.ok === 'boolean' &&
    typeof candidate.text === 'function' &&
    candidate.headers != null
  );
};

/** Returns the payload, or throws GatewayError when the result is a Response. */
export const unwrap = <T>(result: T | Response): T => {
  if (isHttpResponse(result)) {
    throw new GatewayError(result.status, result.statusText, result);
  }
  return result as T;
};

/**
 * The message to show the user: ours for a request timeout, else the fallback.
 * Never the backend's or the browser's own text (status codes, exceptions).
 */
export const errorMessage = (error: unknown, fallback: string): string =>
  error instanceof RequestTimeoutError ? error.message : fallback;

/**
 * The agency's pool status when the gateway refused the call because the
 * token limit is reached (429 TOKEN_LIMIT_REACHED), else null.
 */
export const tokenLimitFromError = async (
  error: unknown,
): Promise<TokenStatus | null> =>
  isGatewayError(error) && error.status === 429 && error.response
    ? readTokenLimitReached(error.response)
    : null;
